import React, { useState, useEffect, useMemo } from 'react';
import { UserData, AgeDetail, AppSettings, Mood } from '../types';
import { calculateAge, calculateZodiac } from '../utils/calculator';
import { searchPlace, getTimezone } from '../services/mockApi';
import { MapPin, Clock, Loader2, Sparkles, ArrowRight, MessageCircle, Zap, Activity, Info } from 'lucide-react';
import CustomDatePicker from '../components/CustomDatePicker';
import { ZODIAC_DATA, MOOD_THEMES } from '../constants';
import { calculateDailyEnergy, getTimeUntilAvailable } from '../utils/cosmic';
import { useSetMeta } from '../hooks/useSetMeta';
import { generateAIContent } from '../services/ai';
import AdSpace from '../components/AdSpace';
import homeData from '../src/content/home.json';
import { ARTICLES } from './BlogIndex';
import { BookOpen } from 'lucide-react';

interface HomeProps {
  userData: UserData;
  setUserData: (data: UserData) => void;
  settings?: AppSettings;
  updateSettings?: (s: Partial<AppSettings>) => void;
}

const MOOD_CONTEXT: Record<string, string> = {
    'Calm': "Your calm state helps you feel this energy more clearly.",
    'Confused': "This energy may feel uncertain today — clarity comes with time.",
    'Excited': "Your excitement amplifies how this energy is experienced.",
    'Anxious': "This energy may feel quieter today — take things gently."
};

const Home: React.FC<HomeProps> = ({ userData, setUserData, settings, updateSettings }) => {
  // Set SEO meta tags for home page
  useSetMeta({
    title: 'Age Calculator - Calculate Exact Age in Years, Months, Days',
    description: 'Free online age calculator. Calculate your exact age in years, months, days, hours, minutes and seconds. Live updating age counter with zodiac insight.',
    ogImage: 'https://zodiage.com/logo.png'
  });

  const [age, setAge] = useState<AgeDetail | null>(null);
  const [placeQuery, setPlaceQuery] = useState(userData.birthPlace);
  const [suggestions, setSuggestions] = useState<{address: string, lat: number, lng: number}[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);

  // Oracle State
  const [oracleQuery, setOracleQuery] = useState('');
  const [oracleResponse, setOracleResponse] = useState(() => {
    if (settings?.lastOracleResponse && settings?.lastOracleAskTimestamp) {
                 const status = getTimeUntilAvailable(settings.lastOracleAskTimestamp);
                 if (status.locked) return settings.lastOracleResponse;
    }
    return '';
  });
  
  const [isOracleThinking, setIsOracleThinking] = useState(false);
  const [oracleTimer, setOracleTimer] = useState('');
  const [isOracleLocked, setIsOracleLocked] = useState(false);

  // Mood UX State
  const [animatingMood, setAnimatingMood] = useState(false);
  const [showEnergyInfo, setShowEnergyInfo] = useState(false);

  // Daily Energy Score
  const dailyEnergy = useMemo(() => calculateDailyEnergy(userData.birthDate), [userData.birthDate]);

    useEffect(() => {
        const checkOracleLock = () => {
                const status = getTimeUntilAvailable(settings?.lastOracleAskTimestamp || null);
                setIsOracleLocked(status.locked);
                setOracleTimer(status.text);
        };
    
        checkOracleLock();
        const interval = setInterval(checkOracleLock, 1000);
        return () => clearInterval(interval);
    }, [settings?.lastOracleAskTimestamp]);

  useEffect(() => {
    if (!userData.birthDate) return;
    const tick = () => {
      if (userData.birthDate) {
        setAge(calculateAge(userData.birthDate));
      }
    };
    tick(); 
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [userData.birthDate, isCalculated]);

  const handleDateChange = (date: Date) => {
    if (userData.includeTime && userData.birthDate) {
        date.setHours(userData.birthDate.getHours());
        date.setMinutes(userData.birthDate.getMinutes());
    }
    setUserData({ ...userData, birthDate: date });
    setIsCalculated(false);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userData.birthDate) return;
    const [hours, minutes] = e.target.value.split(':').map(Number);
    const newDate = new Date(userData.birthDate);
    newDate.setHours(hours);
    newDate.setMinutes(minutes);
    setUserData({ ...userData, birthDate: newDate, includeTime: true });
    setIsCalculated(false);
  };

  const handlePlaceSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPlaceQuery(val);
    if (val.length > 2) {
      setIsSearching(true);
      const results = await searchPlace(val);
      setSuggestions(results);
      setIsSearching(false);
    } else {
      setSuggestions([]);
    }
  };

  const selectPlace = async (place: {address: string, lat: number, lng: number}, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Update local query state and clear suggestions immediately
    setPlaceQuery(place.address);
    setSuggestions([]);
    
    // Fetch timezone and update user data
    const tz = await getTimezone(place.lat, place.lng);
    setUserData({ 
        ...userData, 
        birthPlace: place.address, 
        coordinates: { lat: place.lat, lng: place.lng },
        timezone: tz 
    });
    setIsCalculated(false);
  };

  const handleCalculate = () => {
    if(userData.birthDate) {
        setIsCalculated(true);
        setTimeout(() => {
            const results = document.getElementById('results-section');
            if(results) results.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }
  };

  const handleTarotClick = () => {
      window.history.pushState({}, '', '/tarot'); window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleMoodSelect = (mood: Mood) => {
      if (updateSettings) {
          updateSettings({ currentMood: mood });
      }
      setAnimatingMood(true);
      setTimeout(() => setAnimatingMood(false), 700);
  };

  const handleOracleAsk = async () => {
      if(!oracleQuery.trim()) return;
      const status = getTimeUntilAvailable(settings?.lastOracleAskTimestamp || null);
      if (status.locked) return;

      setIsOracleThinking(true);
      setOracleResponse('');

      try {
          const prompt = `
            User asks: "${oracleQuery}".
            User mood: ${settings?.currentMood || 'Neutral'}.
            Reply as ZodiAge, a calm, mystical guide. 
            One short, reflective sentence only. No greeting. 
            Encouraging and metaphorical.
          `;
          const responseText = await generateAIContent(prompt);
          if(responseText) {
              setOracleResponse(responseText);
              if (updateSettings) {
                  updateSettings({ 
                      lastOracleAskTimestamp: Date.now(),
                      lastOracleResponse: responseText 
                  });
              }
          }
      } catch(e) {
          setOracleResponse("The mists are thick today. Look inward for your answer.");
      } finally {
          setIsOracleThinking(false);
      }
  };

  const clearOracleResponse = () => {
      setOracleResponse('');
      setOracleQuery('');
      if (updateSettings) updateSettings({ lastOracleResponse: null });
  };

  const userSign = useMemo(() => {
    return userData.birthDate ? calculateZodiac(userData.birthDate) : null;
  }, [userData.birthDate]);

  const zodiacData = userSign ? ZODIAC_DATA[userSign] : null;
  const ageInYears = age ? age.years + (age.months / 12) + (age.days / 365) : 0;
  
  useEffect(() => {
    if (ageInYears > userData.lifeExpectancy) {
        setUserData({ ...userData, lifeExpectancy: Math.floor(ageInYears) + 5 });
    }
  }, [ageInYears, userData.lifeExpectancy]);

  const percentLived = Math.min(100, Math.max(0, (ageInYears / userData.lifeExpectancy) * 100));
  const percentRemaining = 100 - percentLived;

  const handleExpectancyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserData({ ...userData, lifeExpectancy: parseFloat(e.target.value) });
  };

  const currentMoodTheme = settings?.currentMood ? MOOD_THEMES[settings.currentMood] : null;

  return (
    <div className="space-y-12 animate-float">
      <div id="calculator-section" className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div className="bg-card/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 shadow-xl transition-all hover:border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Activity size={18} className="text-[var(--season-secondary)]" /> How are you feeling today?
              </h3>
              <div className="flex justify-between gap-2">
                  {(Object.keys(MOOD_THEMES) as Mood[]).map((mood) => {
                      if (!mood) return null;
                      const isSelected = settings?.currentMood === mood;
                      const theme = MOOD_THEMES[mood];
                      return (
                          <button
                            key={mood}
                            onClick={() => handleMoodSelect(mood)}
                            className={`flex-1 py-3 px-2 rounded-xl border transition-all duration-300 flex flex-col items-center gap-1 active:scale-95 ${isSelected ? 'bg-white/10 border-[var(--season-primary)] scale-105 shadow-[0_0_10px_var(--season-glow)]' : 'bg-black/20 border-gray-800 hover:bg-white/5'}`}
                          >
                              <span className="text-2xl">{theme.icon}</span>
                              <span className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-gray-400'}`}>{mood}</span>
                          </button>
                      );
                  })}
              </div>
          </div>

          <div 
            className={`bg-card/50 backdrop-blur-sm rounded-2xl p-6 border shadow-xl flex items-center justify-between relative overflow-hidden transition-all duration-500 ${animatingMood ? 'scale-[1.02] shadow-[0_0_20px_var(--season-glow)]' : ''}`}
            style={{ 
                borderColor: currentMoodTheme ? currentMoodTheme.color : '#1f2937',
                boxShadow: currentMoodTheme ? `0 0 15px ${currentMoodTheme.glow}` : undefined
            }}
          >
               <div className="relative z-10 max-w-[60%]">
                   <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <Zap size={18} style={{ color: currentMoodTheme ? currentMoodTheme.color : '#FACC15' }} /> Cosmic Energy
                        </h3>
                        <button onClick={() => setShowEnergyInfo(!showEnergyInfo)} className="text-gray-500 hover:text-white transition-colors">
                            <Info size={14} />
                        </button>
                   </div>
                   {showEnergyInfo && (
                       <div className="absolute top-full left-0 mt-2 bg-black/90 p-3 rounded-lg text-xs text-gray-300 border border-gray-700 z-50 w-56 animate-in fade-in zoom-in-95">
                           Cosmic Energy changes daily based on the stars. Your mood affects how you experience it, not the score itself.
                       </div>
                   )}
                   <div className="text-4xl font-display font-bold transition-colors duration-500" style={{ color: currentMoodTheme ? currentMoodTheme.color : 'var(--season-primary)' }}>
                       {dailyEnergy}%
                   </div>
                   <p className={`text-xs mt-2 transition-all duration-500 ${settings?.currentMood ? 'text-gray-200' : 'text-gray-500 italic'}`}>
                        {settings?.currentMood ? MOOD_CONTEXT[settings.currentMood] : "Select your mood to see how it aligns."}
                   </p>
               </div>
               <div className="relative w-24 h-24 flex items-center justify-center">
                   <svg className="w-full h-full transform -rotate-90">
                       <circle cx="48" cy="48" r="36" stroke="#1f2937" strokeWidth="8" fill="transparent" />
                       <circle 
                            cx="48" cy="48" r="36" 
                            stroke={currentMoodTheme ? currentMoodTheme.color : "var(--season-primary)"}
                            strokeWidth="8" 
                            fill="transparent" 
                            strokeDasharray="226.19" 
                            strokeDashoffset={226.19 * (1 - dailyEnergy/100)}
                            className="transition-all duration-1000 ease-out"
                        />
                   </svg>
                   <div className="absolute inset-0 flex items-center justify-center">
                       <Sparkles size={20} className="text-white animate-pulse" />
                   </div>
               </div>
          </div>
      </div>

      <div className="bg-card/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-800 shadow-xl max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0 bg-purple-900/20 p-4 rounded-full">
              <MessageCircle size={32} className="text-[var(--season-secondary)]" />
          </div>
          <div className="flex-1 w-full">
              <div className="flex justify-between items-center mb-2">
                 <h3 className="text-lg font-bold text-white">Ask ZodiAge</h3>
                 {isOracleLocked && (
                     <div className="flex items-center gap-2 text-xs font-mono bg-gradient-to-r from-purple-600 to-pink-500 text-white px-3 py-1 rounded-full border border-purple-400/50 shadow-lg shadow-purple-600/40 animate-in fade-in font-bold">
                         <Clock size={12} className="animate-spin" /> {oracleTimer}
                     </div>
                 )}
              </div>
              {!oracleResponse ? (
                  <div className="relative">
                      <div className={`flex gap-2 transition-all duration-300 ${isOracleLocked ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                          <input 
                            type="text" 
                            placeholder="What should I focus on today?" 
                            className="flex-1 bg-black/40 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-[var(--season-primary)] outline-none"
                            value={oracleQuery}
                            onChange={(e) => setOracleQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleOracleAsk()}
                            disabled={isOracleLocked}
                          />
                          <button onClick={handleOracleAsk} disabled={isOracleThinking || !oracleQuery || isOracleLocked} className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold px-4 py-2 rounded-lg hover:from-purple-500 hover:to-pink-400 disabled:opacity-50 disabled:from-purple-700 disabled:to-pink-600 transition-all shadow-lg shadow-purple-600/40">
                              {isOracleThinking ? <Loader2 className="animate-spin" size={20}/> : <Sparkles size={20}/>}
                          </button>
                      </div>
                      {isOracleLocked && (
                         <div className="absolute inset-0 flex items-center justify-center">
                             <span className="text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-full backdrop-blur-sm border border-purple-400/50 shadow-lg shadow-purple-600/40">
                                 ⏱ Recharging in {oracleTimer}
                             </span>
                         </div>
                      )}
                  </div>
              ) : (
                  <div className="animate-in fade-in slide-in-from-bottom-2">
                      <p className="text-[var(--season-primary)] italic text-lg font-medium leading-relaxed">
                          "{oracleResponse}"
                      </p>
                      <button onClick={clearOracleResponse} className="text-xs text-gray-500 mt-2 hover:text-white underline">
                          {isOracleLocked ? "Clear & Wait" : "Ask another question"}
                      </button>
                  </div>
              )}
          </div>
      </div>

      <div className="bg-card rounded-2xl p-8 border border-gray-800 shadow-2xl max-w-4xl mx-auto overflow-visible">
        <div className="mb-8">
            <h2 className="text-2xl font-bold text-secondary mb-2">Enter Your Birth Details</h2>
            <p className="text-gray-400 text-sm">Let's begin your journey through time.</p>
        </div>
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-30">
                <div className="space-y-2 z-40">
                    <label className="text-sm text-white font-medium">Date of Birth</label>
                    <CustomDatePicker value={userData.birthDate} onChange={handleDateChange} />
                </div>
                <div className="space-y-2 z-40">
                    <label className="text-sm text-white font-medium">Time of Birth (optional)</label>
                    <div className="relative">
                        <Clock className="absolute left-3 top-3.5 text-gray-400" size={18} />
                        <input type="time" className="w-full bg-input border border-gray-700 rounded-lg p-3 pl-10 text-white placeholder-gray-500 focus:border-[var(--season-primary)] focus:ring-1 focus:ring-[var(--season-primary)] outline-none transition-all h-[50px]" onChange={handleTimeChange} />
                    </div>
                </div>
            </div>
            <div className="space-y-2 relative z-20">
                <label className="text-sm text-white font-medium">Place of Birth (for accurate results)</label>
                <div className="relative">
                    <MapPin className="absolute left-3 top-3.5 text-gray-400" size={18} />
                    <input type="text" placeholder="e.g., Paris, France" className="w-full bg-input border border-gray-700 rounded-lg p-3 pl-10 text-white placeholder-gray-500 focus:border-[var(--season-primary)] focus:ring-1 focus:ring-[var(--season-primary)] outline-none transition-all h-[50px]" value={placeQuery} onChange={handlePlaceSearch} />
                    {isSearching && <Loader2 className="absolute right-3 top-3.5 text-[var(--season-primary)] animate-spin" size={18} />}
                </div>
                {suggestions.length > 0 && (
                <div className="absolute top-full left-0 w-full bg-card border border-gray-700 rounded-lg mt-1 z-50 shadow-xl max-h-40 overflow-y-auto">
                    {suggestions.map((s, idx) => (
                    <div 
                        key={idx} 
                        className="p-3 hover:bg-gray-800 cursor-pointer text-sm text-gray-300 transition-colors"
                        onClick={(e) => selectPlace(s, e)}
                    >
                        {s.address}
                    </div>
                    ))}
                </div>
                )}
            </div>
            <button onClick={handleCalculate} className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-bold text-lg py-3 rounded-lg hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 transition-all shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70 mt-4 relative z-10">
                Calculate My Age
            </button>
        </div>
      </div>

      {isCalculated && age && (
        <div id="results-section" className="max-w-6xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-10 duration-700">
            <div>
                <h2 className="text-2xl font-bold text-white mb-1">Your Precise Age</h2>
                <p className="text-gray-400 text-sm">Updating in real-time.</p>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                <ResultBox label="YEARS" value={age.years} />
                <ResultBox label="MONTHS" value={age.months} />
                <ResultBox label="DAYS" value={age.days} />
                <ResultBox label="HOURS" value={age.hours} />
                <ResultBox label="MINUTES" value={age.minutes} />
                <ResultBox label="SECONDS" value={age.seconds} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <TotalBox label="Total Months" value={age.totalMonths.toLocaleString()} />
                <TotalBox label="Total Days" value={age.totalDays.toLocaleString()} />
                <TotalBox label="Total Hours" value={age.totalHours.toLocaleString()} />
                <TotalBox label="Total Seconds" value={age.totalSeconds.toLocaleString()} />
            </div>
            <div className="flex justify-center py-4 animate-in fade-in zoom-in-95 duration-700 delay-300">
                 <button onClick={handleTarotClick} className="group relative bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-bold py-4 px-8 rounded-full shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70 transition-all transform hover:scale-105 flex items-center gap-3 overflow-hidden hover:from-purple-500 hover:via-purple-400 hover:to-pink-400">
                     <Sparkles size={20} className="animate-pulse" />
                     <span>Pick a Random Tarot Card</span>
                     <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                     <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent z-10"></div>
                 </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#040818] rounded-xl border border-gray-800 shadow-2xl overflow-hidden relative min-h-[500px] flex flex-col">
                    <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-[#1E1E2F] to-transparent opacity-50 z-0"></div>
                    <div className="relative z-10 p-6 flex-shrink-0">
                        <h3 className="text-2xl font-bold text-white mb-1">Your Zodiac Sign</h3>
                        <p className="text-gray-400">Cosmic Alignment</p>
                    </div>
                    <div className="relative flex-1 flex flex-col items-center justify-center p-8 animate-in zoom-in-50 duration-700">
                        {userSign && zodiacData && (
                            <>
                                <div className="w-24 h-24 rounded-3xl bg-[var(--season-secondary)] flex items-center justify-center text-white shadow-[0_0_40px_rgba(168,85,247,0.6)] mb-6 transform hover:scale-110 transition-transform duration-500">
                                    <span className="text-5xl drop-shadow-md">{zodiacData.symbol}</span>
                                </div>
                                <h3 className="text-5xl font-display font-bold text-white mb-2 drop-shadow-lg text-center">{userSign}</h3>
                                <p className="text-sm font-bold uppercase tracking-[0.2em] text-[var(--season-primary)] mb-6 text-center">{zodiacData.dateRange}</p>
                                <div className="px-6 py-2 rounded-full border border-white/20 bg-white/5 text-gray-300 font-bold uppercase tracking-wider backdrop-blur-sm">{zodiacData.element}</div>
                            </>
                        )}
                    </div>
                    <div className="relative z-10 p-6 bg-gradient-to-t from-[#020410] via-[#020410]/95 to-transparent pt-12 mt-auto text-center">
                        <p className="text-gray-300 text-sm leading-relaxed font-medium">{userSign && zodiacData && zodiacData.description}</p>
                    </div>
                </div>
                <div className="bg-card rounded-xl p-6 border border-gray-800 shadow-lg flex flex-col justify-between h-[500px]">
                    <div>
                        <h3 className="text-2xl font-bold text-white mb-1">Life Progress</h3>
                        <p className="text-gray-400 mb-8">Based on a life expectancy of <span className="text-[#FFC82C] font-bold">{userData.lifeExpectancy}</span> years.</p>
                        <div className="w-full h-4 bg-[#0F172A] rounded-full overflow-hidden mb-2 border border-gray-800">
                             <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${percentLived}%`, background: 'linear-gradient(to right, #FFC82C, #FFD700)' }}></div>
                        </div>
                        <div className="flex justify-between text-sm text-gray-400 mb-12">
                            <span style={{ color: '#FFC82C' }}>{percentLived.toFixed(2)}% Lived</span>
                            <span>{percentRemaining.toFixed(2)}% Remaining</span>
                        </div>
                    </div>
                    <div>
                        <h4 className="text-[#FFC82C] font-bold mb-4">Adjust Life Expectancy</h4>
                        <div className="relative h-2 bg-[#0F172A] rounded-full">
                             <input type="range" min="50" max="150" step="1" value={userData.lifeExpectancy} onChange={handleExpectancyChange} className="absolute w-full h-full opacity-0 cursor-pointer z-20" style={{ accentColor: '#FFC82C' }} />
                            <div className="absolute top-0 left-0 h-full rounded-full z-10 pointer-events-none" style={{ width: `${((userData.lifeExpectancy - 50) / 100) * 100}%`, background: '#FFC82C' }}></div>
                            <div className="absolute w-4 h-4 bg-[#020410] border-2 rounded-full top-1/2 -translate-y-1/2 z-10 pointer-events-none shadow-[0_0_10px_rgba(255,200,44,0.6)]" style={{ left: `${((userData.lifeExpectancy - 50) / 100) * 100}%`, borderColor: '#FFC82C' }}></div>
                        </div>
                        <div className="text-right mt-2 text-xs" style={{ color: '#FFC82C' }}>Current: {userData.lifeExpectancy} Years</div>
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* Latest Blog Articles */}
      <div className="max-w-4xl mx-auto mb-16 px-4 md:px-0">
          <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <BookOpen className="text-[var(--season-secondary)]" /> The Cosmic Journal
              </h2>
              <button 
                onClick={() => { window.history.pushState({}, '', '/blog'); window.dispatchEvent(new PopStateEvent('popstate')); }} 
                className="text-sm font-bold text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
              >
                  View All <ArrowRight size={14} />
              </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ARTICLES.slice(0, 3).map(article => (
                  <button
                      key={article.slug}
                      onClick={() => { window.history.pushState({}, '', `/blog/${article.slug}`); window.dispatchEvent(new PopStateEvent('popstate')); }}
                      className="bg-card/30 border border-gray-800 rounded-2xl p-6 text-left hover:border-[var(--season-secondary)] hover:bg-card/50 transition-all group flex flex-col items-start h-[220px]"
                  >
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--season-secondary)] mb-3">{article.category}</span>
                        <h3 className="text-lg font-bold text-white mb-2 leading-tight group-hover:text-[var(--season-primary)] transition-colors line-clamp-3">{article.title}</h3>
                        <div className="mt-auto text-xs font-bold text-gray-500 group-hover:text-[var(--season-primary)] transition-colors flex items-center w-full">
                            Read Article <ArrowRight size={12} className="ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                  </button>
              ))}
          </div>
      </div>

      {/* SEO Educational Content (AdSense Compliance) */}
      <article className="max-w-4xl mx-auto mb-12 mt-16">
          <div className="bg-cosmic-800/30 p-8 md:p-12 rounded-2xl border border-cosmic-700/50">
              <div className="seo-content" dangerouslySetInnerHTML={{ __html: homeData.html }} />
              
              {/* Multiplex Ad for Home Page Footer */}
              <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
          </div>
      </article>
    </div>
  );
};

const ResultBox = ({ label, value }: { label: string, value: number }) => (
    <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-4 md:p-6 flex flex-col items-center justify-center min-h-[100px] md:min-h-[120px] shadow-lg overflow-hidden">
        <span className="text-xs text-purple-200 uppercase tracking-widest mb-2">{label}</span>
        <span className="text-2xl md:text-4xl font-bold text-white font-display truncate max-w-full">{value.toLocaleString()}</span>
    </div>
);

const TotalBox = ({ label, value }: { label: string, value: string }) => (
    <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-3 md:p-4 shadow-lg h-full overflow-hidden">
        <div className="text-xs text-purple-200 mb-1 truncate">{label}</div>
        <div className="text-sm md:text-lg lg:text-xl font-bold text-white truncate">{value}</div>
    </div>
);

export default Home;
